import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../services/supabaseClient';
import { UserProfile } from '../types';
import { sameCompany } from '../services/companyScope';
import { describeError } from '../services/errorMessage';
import { customerToFeature, projectToFeature } from '../services/mapFeatures';
import {
    RouteDay,
    UserActivityDay,
    UserActivityEvent,
    UserActivityEventType,
    UserActivityCloseReason,
    UserActivityOpenReason,
    UserActivitySummary,
} from '../services/userActivityTypes';
import {
    ProjectDurationRank,
    SessionProjectRef,
    averageSessionMs,
    boundaryCandidates,
    buildRouteDays,
    fillDayRange,
    followingCloseEvents,
    localDayKey,
    precedingOpenEvents,
    summarizeAllUsers,
    topProjectsByDuration,
} from '../services/userActivityStats';

// Ladeschicht des Admin-Tabs „Nutzeraktivitaet".
//
// Hier steckt das IO und der Zustand; gerechnet wird pur in
// services/userActivityStats.ts. Die Komponenten darunter sind duenne
// Container (Standing Decision: „duenne Container, Logik in Hooks + pure
// Funktionen"). Dieser Hook rendert nichts.
//
// KOORDINATENHERKUNFT (bindend): die Route verbindet PROJEKTADRESSEN in
// zeitlicher Reihenfolge. Gelesen wird derselbe Weg wie ueberall sonst im
// Repo — services/mapFeatures.projectToFeature aus
// `projects.data.metadata.globalData.locationCoordinates`, ersatzweise
// `customers.lat/lng` ueber `projects.customer_id` (SQL Nr. 13,
// supabase/geo_foundation.sql). Kein navigator.geolocation, kein Geraete-GPS.

// ─── Filter ─────────────────────────────────────────────────────────────────

export const ZEITRAUM_VORGABEN = [7, 30, 90] as const;

export interface UserActivityFilter {
    /** 7 / 30 / 90 Tage. Wird ignoriert, sobald `von` ODER `bis` gesetzt ist. */
    tage: number;
    /**
     * 'YYYY-MM-DD' — freier Zeitraum, beide leer = Vorgabe `tage`. Eine
     * einzelne Grenze genuegt, die andere wird aufgefuellt (siehe
     * `zeitfenster`).
     */
    von: string;
    bis: string;
    /** 'all' oder eine Profil-id. */
    userId: string;
}

export const STANDARD_FILTER: UserActivityFilter = { tage: 30, von: '', bis: '', userId: 'all' };

// ─── Abgeleitete Zeilen ─────────────────────────────────────────────────────

/** Eine Zeile der Uebersichtstabelle. */
export interface UserActivityRow {
    userId: string;
    name: string;
    email: string | null;
    summary: UserActivitySummary;
    /** Ø Verweildauer je ABGESCHLOSSENER Sitzung. null = keine abgeschlossene. */
    avgDurationMs: number | null;
}

/**
 * Eine Zeile der Rangliste „Top-Projekte nach Verweildauer".
 * Gerechnet wird pur in userActivityStats.topProjectsByDuration; hier steht
 * nur der Name, unter dem die UI den Typ kennt.
 */
export type TopProjectRow = ProjectDurationRank;

/** Serien fuer die Diagramme, ueber die aktuelle Auswahl zusammengefasst. */
export interface UserActivityChartData {
    perDay: UserActivityDay[];
    hourHistogram: number[];
    weekdayHourHeatmap: number[][];
}

// ─── Hilfen ─────────────────────────────────────────────────────────────────

/** Der Zeitraum, der wirklich abgefragt wird — die Ansicht zeigt ihn an. */
export interface Zeitfenster {
    /**
     * Untere Grenze. `null` = keine — dann beginnt die Auswertung beim ersten
     * ueberhaupt erfassten Ereignis (die Erfassung selbst beginnt mit der
     * Migration Nr. 46, rueckwirkend gibt es nichts).
     */
    vonIso: string | null;
    bisIso: string;
    /** Die freie Eingabe greift. */
    frei: boolean;
    /** Freie Eingabe unbrauchbar (von nach bis) — es greift die Tagesvorgabe. */
    ungueltig: boolean;
}

/**
 * Zeitfenster als ISO-Grenzen. Tagesgrenzen sind LOKAL (Europe/Berlin-Praxis).
 *
 * EINE freie Grenze reicht: die fehlende wird aufgefuellt — oben mit „jetzt",
 * unten mit „keine Grenze", also dem ersten erfassten Ereignis. Vorher musste
 * man beide Felder setzen; bei nur einem Datum fiel die Abfrage stumm auf die
 * Tagesvorgabe zurueck, waehrend kein Vorgabeknopf aktiv aussah — welcher
 * Zeitraum gerade galt, war der Ansicht dann nicht anzusehen.
 */
export const zeitfenster = (filter: UserActivityFilter, jetzt = new Date()): Zeitfenster => {
    const hatFreie = !!(filter.von || filter.bis);
    if (hatFreie) {
        const von = filter.von ? new Date(`${filter.von}T00:00:00`) : null;
        const bis = filter.bis ? new Date(`${filter.bis}T23:59:59.999`) : null;
        const brauchbar =
            (!von || !Number.isNaN(von.getTime())) &&
            (!bis || !Number.isNaN(bis.getTime())) &&
            (!von || !bis || von <= bis);
        if (brauchbar) {
            return {
                vonIso: von ? von.toISOString() : null,
                bisIso: (bis || jetzt).toISOString(),
                frei: true,
                ungueltig: false,
            };
        }
    }

    const tage = Math.max(1, Math.round(filter.tage || 30));
    const von = new Date(jetzt);
    von.setHours(0, 0, 0, 0);
    von.setDate(von.getDate() - (tage - 1));
    return { vonIso: von.toISOString(), bisIso: jetzt.toISOString(), frei: false, ungueltig: hatFreie };
};

const EVENT_TYPEN: UserActivityEventType[] = ['app_start', 'project_open', 'project_close'];
const SCHLIESS_GRUENDE: UserActivityCloseReason[] = [
    'manual', 'idle', 'logout', 'unload', 'switch', 'hidden', 'recovered', 'maintenance',
];
const OEFFNUNGS_GRUENDE: UserActivityOpenReason[] = ['normal', 'resume'];

/**
 * `open_reason` -> `openReason`. Die Spalte gibt es erst seit Migration Nr. 46;
 * NULL bei einer Oeffnung heisst laut Datenvertrag `'normal'` (Vorwaerts-
 * kompatibilitaet, Altzeilen sind normale Oeffnungen). Bei allen anderen
 * Ereignistypen bleibt das Feld null.
 *
 * Ein unbekannter Wert wird zu `'normal'` und nicht verworfen: die Oeffnung hat
 * stattgefunden, nur ihr Grund ist unklar — sie als Wiederaufnahme zu buchen
 * wuerde sie aus „Projekte geoeffnet" streichen, also eine echte Oeffnung
 * verschlucken. Der CHECK der Tabelle laesst ohnehin nur die beiden Werte zu.
 */
const zuOeffnungsgrund = (typ: UserActivityEventType, roh: unknown): UserActivityOpenReason | null => {
    if (typ !== 'project_open') return null;
    const wert = roh ? (String(roh) as UserActivityOpenReason) : null;
    return wert && OEFFNUNGS_GRUENDE.includes(wert) ? wert : 'normal';
};

/**
 * snake_case-Zeile -> Spec-Typ. Unbekannte `event_type`/`close_reason` werden
 * VERWORFEN statt durchgereicht: ein Wert, den die Auswertung nicht kennt,
 * wuerde dort stillschweigend als „irgendwas" mitzaehlen.
 */
const zuEvent = (row: any): UserActivityEvent | null => {
    const typ = String(row?.event_type || '') as UserActivityEventType;
    if (!EVENT_TYPEN.includes(typ)) return null;
    if (!row?.id || !row?.user_id || !row?.session_id || !row?.occurred_at) return null;

    const grund = row.close_reason ? (String(row.close_reason) as UserActivityCloseReason) : null;
    return {
        id: String(row.id),
        userId: String(row.user_id),
        sessionId: String(row.session_id),
        eventType: typ,
        occurredAt: String(row.occurred_at),
        projectId: row.project_id ? String(row.project_id) : null,
        projectNumber: row.project_number ? String(row.project_number) : null,
        projectName: row.project_name ? String(row.project_name) : null,
        closeReason: grund && SCHLIESS_GRUENDE.includes(grund) ? grund : null,
        openReason: zuOeffnungsgrund(typ, row.open_reason),
        companyId: row.company_id ? String(row.company_id) : null,
    };
};

export const nutzerName = (p?: UserProfile | null): string => {
    if (!p) return 'Unbekannt';
    const name = [p.first_name, p.last_name].filter(Boolean).join(' ').trim();
    return name || (p as any).email || 'Unbekannt';
};

/** Fehlt die Tabelle noch (SQL Nr. 46 nicht gefahren), sagen wir das im Klartext. */
const TABELLE_FEHLT = /user_activity_events|does not exist|schema cache|PGRST205|42P01/i;
const istTabelleFehlt = (e: any): boolean =>
    e?.code === '42P01' ||
    e?.code === 'PGRST205' ||
    (typeof e?.message === 'string' && /does not exist|schema cache/i.test(e.message) && TABELLE_FEHLT.test(e.message));

/**
 * PostgREST `in`-Listen bleiben handhabbar, wenn sie in Bloecken laufen.
 *
 * 100 statt 200: eine uuid kostet in der GET-URL rund 39 Byte (36 + Komma +
 * Anfuehrungszeichen). 200 Stueck sind bereits ~7,7 KB und liegen damit keine
 * 500 Byte unter der ueblichen 8-KB-Grenze fuer Request-Zeilen — der Server
 * antwortet dann mit 414 statt mit Daten, und zwar erst in der Praxis mit
 * vielen Nutzern. 100 halten die URL bei rund 4 KB.
 */
const bloecke = <T,>(werte: T[], groesse = 100): T[][] => {
    const out: T[][] = [];
    for (let i = 0; i < werte.length; i += groesse) out.push(werte.slice(i, i + groesse));
    return out;
};

/**
 * Firmenzugehoerigkeit fuer die Sichtbarkeit in diesem Tab.
 *
 * `companyScope.sameCompany` faellt ohne `company_id` auf den Namensvergleich
 * zurueck; zwei Profile OHNE JEDE Firmenangabe treffen sich dort ueber
 * '' === '' und saehen einander. Das Altmuster bleibt unangetastet (es ist
 * repoweit in Gebrauch) — hier gilt „gar keine Firmenangabe" als
 * Nicht-Treffer. Das eigene Profil bleibt selbstverstaendlich sichtbar.
 */
const hatFirmenangabe = (p?: UserProfile | null): boolean =>
    !!((p?.company_id || '').trim() || (p?.company_name || '').trim());

const gleicheFirma = (kandidat: UserProfile, ich: UserProfile): boolean => {
    if (kandidat.id === ich.id) return true;
    if (!hatFirmenangabe(kandidat) || !hatFirmenangabe(ich)) return false;
    return sameCompany(kandidat, ich);
};

/**
 * Was der Firmenfilter WEGGEWORFEN hat.
 *
 * Ein Profil ohne jede Firmenangabe faellt oben heraus, und mit ihm faellt der
 * Nutzer aus der Abfrage — seine Ereignisse werden gar nicht erst geladen.
 * Solange der Bestand nicht nachgetragen ist (`company_id`/`company_name` am
 * Profil), meldet der Reiter sonst „Keine Daten im Zeitraum", obwohl Daten da
 * sind. Die Zahl wird deshalb mitgefuehrt und in der Filterleiste genannt.
 */
interface Firmenfilterrest {
    /** Fremde Profile, die allein wegen fehlender Firmenangabe fehlen. */
    verworfen: number;
    /** Am EIGENEN Profil fehlt die Angabe — dann bleibt nur die eigene Zeile. */
    eigeneFehlt: boolean;
}

const firmenfilterrest = (alle: UserProfile[], ich: UserProfile): Firmenfilterrest => {
    // super_admin filtert nicht nach Firma, dort wird nichts verworfen.
    if (ich.role === 'super_admin') return { verworfen: 0, eigeneFehlt: false };
    const eigeneFehlt = !hatFirmenangabe(ich);
    const verworfen = alle.filter((u) => u.id !== ich.id && !hatFirmenangabe(u)).length;
    return { verworfen, eigeneFehlt };
};

// ─── Ereignisse laden ───────────────────────────────────────────────────────

/**
 * PostgREST liefert hoechstens 1000 Zeilen je Anfrage und sagt das nicht — ein
 * hohes `.limit()` aendert daran nichts (docs/asplan-modul-plan.md, Lehre aus
 * #37). Gelesen wird darum seitenweise, im Muster von
 * services/cloudService.ts (`.range(von, von + SEITE - 1)` bis eine Seite
 * kuerzer als SEITE zurueckkommt).
 */
const EVENT_SEITE = 1000;

/**
 * Harte Obergrenze der geladenen Ereignisse. Greift sie, sagt die Ansicht das
 * an — stilles Abschneiden verbietet sich, weil eine gestutzte Reihe wie eine
 * vollstaendige aussieht.
 *
 * Gelesen wird deshalb ABSTEIGEND: schneidet die Grenze, fehlt der ALTE Rand.
 * Fehlte der junge, blieben die zugehoerigen `project_close`-Ereignisse aus
 * und jede laufende Sitzung saehe „offen" aus — genau die Falschaussage, die
 * das stille 1000er-Limit erzeugt hat. Ein `project_close` ohne Gegenstueck
 * wird dagegen laut Datenvertrag schlicht verworfen.
 *
 * Die Grenze wird je NUTZERBLOCK gefuehrt (siehe `ladeEvents`), nicht als ein
 * gemeinsamer Topf ueber alle Bloecke.
 */
export const EVENT_OBERGRENZE = 50000;

const EVENT_SPALTEN =
    'id, user_id, session_id, event_type, occurred_at, project_id, project_number, project_name, close_reason, open_reason, company_id';

/** Zeitliche Ordnung ueber Bloecke hinweg; bei Gleichstand stabil nach id. */
const nachZeitpunkt = (a: UserActivityEvent, b: UserActivityEvent): number => {
    const d = Date.parse(a.occurredAt) - Date.parse(b.occurredAt);
    if (d) return d;
    return a.id === b.id ? 0 : a.id < b.id ? -1 : 1;
};

/** Eine Seite Ereignisse eines Nutzerblocks, absteigend nach Zeitpunkt. */
const eventAbfrage = (vonIso: string | null, bisIso: string, teil: string[]) => {
    let abfrage = supabase
        .from('user_activity_events')
        .select(EVENT_SPALTEN)
        .lte('occurred_at', bisIso)
        .in('user_id', teil);
    if (vonIso) abfrage = abfrage.gte('occurred_at', vonIso);
    return (
        abfrage
            .order('occurred_at', { ascending: false })
            // Zweiter Schluessel: bei gleichem Zeitstempel entscheidet die
            // Datenbank sonst je Seite neu, und eine Zeile faellt zwischen
            // zwei Seiten heraus oder erscheint doppelt.
            .order('id', { ascending: false })
    );
};

export interface EventLadung {
    events: UserActivityEvent[];
    /** Mindestens ein Nutzerblock endet vor dem Anfang des Zeitraums. */
    abgeschnitten: boolean;
    /**
     * Obergrenze der betroffenen Nutzer: alle Nutzer der gestutzten Bloecke.
     * Innerhalb eines Blocks laesst sich nicht sagen, wessen Zeilen fehlen —
     * die Zahl ist deshalb bewusst eine obere Schranke und wird auch so
     * beschriftet.
     */
    abgeschnitteneNutzer: number;
}

/**
 * Ereignisse des Zeitraums fuer eine feste Nutzermenge.
 *
 * Der Nutzerfilter steht in der ABFRAGE und nicht erst danach in JS: sonst
 * laedt ein Firmen-Admin die Zeilen aller Mandanten in den Browser und
 * verbraucht mit fremden Daten das Zeilenbudget der eigenen Firma.
 *
 * ZEILENBUDGET JE BLOCK (nicht global): vorher lief EIN Zaehler ueber alle
 * Bloecke, und wer die Grenze riss, beendete die ganze Schleife. Alle weiteren
 * Nutzerbloecke wurden nie abgefragt — diese Nutzer standen anschliessend
 * voellig ohne Aktivitaet da, waehrend das Banner lediglich „die aeltesten Tage
 * fehlen" behauptete. Jetzt bekommt jeder Block seinen Anteil, mindestens aber
 * eine Seite; kein Block bleibt ungelesen. Bei sehr vielen Bloecken darf die
 * Summe die Obergrenze dadurch ueberschreiten — ein Nutzer, der stillschweigend
 * leer bleibt, waere der teurere Fehler.
 */
const ladeEvents = async (
    vonIso: string | null,
    bisIso: string,
    nutzerIds: string[],
): Promise<EventLadung> => {
    const gesammelt: UserActivityEvent[] = [];
    let abgeschnitten = false;
    let abgeschnitteneNutzer = 0;
    if (nutzerIds.length === 0) return { events: gesammelt, abgeschnitten, abgeschnitteneNutzer };

    const teile = bloecke(nutzerIds);
    const budget = Math.max(EVENT_SEITE, Math.floor(EVENT_OBERGRENZE / teile.length));

    for (const teil of teile) {
        let gelesen = 0;
        for (let von = 0; ; von += EVENT_SEITE) {
            const { data, error } = await eventAbfrage(vonIso, bisIso, teil).range(von, von + EVENT_SEITE - 1);
            if (error) throw error;

            const seite = data || [];
            gelesen += seite.length;
            seite.forEach((row: any) => {
                const event = zuEvent(row);
                if (event) gesammelt.push(event);
            });

            // Kurze Seite = die Reihe ist zu Ende, nichts fehlt.
            if (seite.length < EVENT_SEITE) break;
            if (gelesen >= budget) {
                // Erst bei NACHGEWIESENEM Ueberhang warnen: eine einzelne
                // Probezeile hinter der Grenze. Geht die Reihe exakt auf (genau
                // budget Ereignisse), ist sie vollstaendig — das alte `>=`
                // allein haette dort ein Warnbanner ueber vollstaendige Daten
                // gehaengt.
                const { data: probe, error: probeFehler } = await eventAbfrage(vonIso, bisIso, teil).range(
                    gelesen,
                    gelesen,
                );
                if (probeFehler) throw probeFehler;
                if ((probe || []).length > 0) {
                    abgeschnitten = true;
                    abgeschnitteneNutzer += teil.length;
                }
                break;
            }
        }
    }

    // Gelesen wird absteigend und in Bloecken; die Auswertung erwartet EINEN
    // aufsteigenden Strom.
    gesammelt.sort(nachZeitpunkt);
    return { events: gesammelt, abgeschnitten, abgeschnitteneNutzer };
};

// ─── Randereignisse ─────────────────────────────────────────────────────────
//
// Die Abfrage oben begrenzt auf [vonIso, bisIso]. Zwei Sitzungsarten liegen
// dadurch nur zur Haelfte in der Reihe:
//
//   (a) VOR vonIso geoeffnet, INNERHALB geschlossen. Das Oeffnen fehlt, die
//       Paarung verwirft das Schliessen — und ein Zeitraumrand liest sich
//       anschliessend wie ein Datenfehler.
//   (b) INNERHALB geoeffnet, NACH bisIso geschlossen. Das Schliessen fehlt, die
//       Sitzung steht auf Dauer als „offen", obwohl sie laengst zu ist.
//
// Beides wird NACHGESEHEN statt geschaetzt. Gefragt wird gezielt: nur nach den
// Browser-Sitzungen, die an einem der beiden Raender wirklich haengen, und
// eingespeist wird nur das eine fehlende Gegenstueck je Paar
// (userActivityStats.precedingOpenEvents / followingCloseEvents). Der Rest der
// Vorgeschichte gehoert nicht in diesen Zeitraum.
//
// Der Nachlauf ist damit an die Daten gebunden und nicht an ihre Menge: keine
// Kandidaten, keine Abfrage. Er laeuft im selben seitenweisen Muster wie die
// Hauptabfrage (Block, `.range`, kurze Seite = Ende) und hat sein eigenes,
// kleines Budget.

/**
 * Obergrenze der nachgeladenen Randzeilen, ueber alle Bloecke verteilt.
 *
 * Bewusst klein gegen EVENT_OBERGRENZE: gesucht wird je Paar EIN Gegenstueck,
 * und eine Browser-Sitzung ist kurzlebig. Greift die Grenze doch, wird das
 * gemeldet (`unvollstaendig`) statt still hingenommen — die betroffenen Raender
 * bleiben dann so stehen, wie sie ohne Nachladen aussaehen.
 */
export const RAND_OBERGRENZE = 5000;

type Randrichtung = 'vor' | 'nach';

/** Eine Seite der Randabfrage. Nur `session_id` als Liste — zwei uuid-Listen in
 *  einer GET-URL sprengen die 8-KB-Grenze (siehe `bloecke`). */
const randSeite = (richtung: Randrichtung, grenzeIso: string, teil: string[], von: number, bis: number) => {
    const basis = supabase.from('user_activity_events').select(EVENT_SPALTEN).in('session_id', teil);
    const gefiltert =
        richtung === 'vor' ? basis.lt('occurred_at', grenzeIso) : basis.gt('occurred_at', grenzeIso);
    // Rueckwaerts vom Anfang, vorwaerts vom Ende: in beiden Faellen liegt das
    // gesuchte Gegenstueck am Anfang der Reihe, die erste Seite genuegt fast
    // immer.
    const aufsteigend = richtung === 'nach';
    return gefiltert
        .order('occurred_at', { ascending: aufsteigend })
        .order('id', { ascending: aufsteigend })
        .range(von, bis);
};

export interface Randladung {
    /** Die Gegenstuecke, die in den Auswertungsstrom duerfen. */
    events: UserActivityEvent[];
    /** Die Obergrenze hat gegriffen — mindestens ein Rand blieb ungeklaert. */
    unvollstaendig: boolean;
}

const LEERE_RANDLADUNG: Randladung = { events: [], unvollstaendig: false };

const ladeRand = async (
    richtung: Randrichtung,
    grenzeIso: string,
    kandidaten: SessionProjectRef[],
    erlaubteNutzer: Set<string>,
): Promise<Randladung> => {
    if (kandidaten.length === 0) return LEERE_RANDLADUNG;

    const nachSession = new Map<string, SessionProjectRef[]>();
    kandidaten.forEach((k) => {
        const liste = nachSession.get(k.sessionId);
        if (liste) liste.push(k);
        else nachSession.set(k.sessionId, [k]);
    });

    const teile = bloecke([...nachSession.keys()]);
    const budget = Math.max(EVENT_SEITE, Math.floor(RAND_OBERGRENZE / teile.length));

    const gefunden: UserActivityEvent[] = [];
    let unvollstaendig = false;

    for (const teil of teile) {
        const teilKandidaten = teil.flatMap((id) => nachSession.get(id) || []);
        const roh: UserActivityEvent[] = [];
        let gelesen = 0;

        for (let von = 0; ; von += EVENT_SEITE) {
            const { data, error } = await randSeite(richtung, grenzeIso, teil, von, von + EVENT_SEITE - 1);
            if (error) throw error;

            const seite = data || [];
            gelesen += seite.length;
            seite.forEach((row: any) => {
                const event = zuEvent(row);
                // Die session_id stammt aus bereits geladenen, nutzergefilterten
                // Zeilen; der Nutzerfilter wird trotzdem wiederholt, damit die
                // Auswertung nicht ueber einen Umweg fremde Zeilen sieht.
                if (event && erlaubteNutzer.has(event.userId)) roh.push(event);
            });

            const stand =
                richtung === 'vor'
                    ? precedingOpenEvents(teilKandidaten, roh)
                    : followingCloseEvents(teilKandidaten, roh);

            // Jedes Gegenstueck gefunden -> weiterlesen brachte nur noch die
            // Vorgeschichte fremder Sitzungen.
            if (stand.unresolved === 0) {
                gefunden.push(...stand.events);
                break;
            }
            // Kurze Seite = die Reihe ist zu Ende. Was jetzt noch fehlt, fehlt
            // wirklich: das Schliessen hat kein Oeffnen (echter Befund), oder
            // die Sitzung ist wirklich noch offen. Beides ist KEINE Luecke.
            if (seite.length < EVENT_SEITE) {
                gefunden.push(...stand.events);
                break;
            }
            if (gelesen >= budget) {
                gefunden.push(...stand.events);
                // Wie in `ladeEvents`: erst bei NACHGEWIESENEM Ueberhang
                // warnen. Geht die Reihe exakt auf, ist sie vollstaendig und
                // ein Vorbehalt in der Ansicht waere schlicht falsch.
                const { data: probe, error: probeFehler } = await randSeite(
                    richtung,
                    grenzeIso,
                    teil,
                    gelesen,
                    gelesen,
                );
                if (probeFehler) throw probeFehler;
                if ((probe || []).length > 0) unvollstaendig = true;
                break;
            }
        }
    }

    return { events: gefunden, unvollstaendig };
};

/**
 * Die fehlenden Gegenstuecke beider Raender zu einer bereits geladenen
 * Fensterreihe.
 *
 * Faellt das Nachladen aus, bleibt die Auswertung stehen: sie sieht dann genau
 * das, was sie ohne diesen Weg saehe. Der Ausfall wird gemeldet, damit die
 * Ansicht die Randzahlen nicht als gesichert ausgibt.
 */
const ladeRandereignisse = async (
    vonIso: string | null,
    bisIso: string,
    fensterEvents: UserActivityEvent[],
    nutzerIds: string[],
): Promise<Randladung> => {
    if (fensterEvents.length === 0 || nutzerIds.length === 0) return LEERE_RANDLADUNG;

    const kanten = boundaryCandidates(fensterEvents);
    const erlaubte = new Set(nutzerIds);
    const events: UserActivityEvent[] = [];
    let unvollstaendig = false;

    try {
        // Ohne untere Grenze gibt es keinen Anfangsrand: die Reihe beginnt beim
        // ersten ueberhaupt erfassten Ereignis.
        if (vonIso) {
            const vorher = await ladeRand('vor', vonIso, kanten.unmatchedCloses, erlaubte);
            events.push(...vorher.events);
            unvollstaendig = unvollstaendig || vorher.unvollstaendig;
        }
        const nachher = await ladeRand('nach', bisIso, kanten.unclosedOpens, erlaubte);
        events.push(...nachher.events);
        unvollstaendig = unvollstaendig || nachher.unvollstaendig;
    } catch (e) {
        console.warn('Randereignisse des Zeitraums nicht ladbar', e);
        return { events: [], unvollstaendig: true };
    }

    return { events, unvollstaendig };
};

// ─── Koordinaten je Projekt ─────────────────────────────────────────────────

export type ProjektKoordinaten = Map<string, { lat: number; lng: number }>;

/**
 * Blockgroesse fuer den Projekt-Select MIT `metadata:data->metadata`.
 * services/cloudService.ts nimmt fuer denselben Select bewusst 50: groessere
 * Antworten brechen ueber HTTP/3 mit ERR_QUIC_PROTOCOL_ERROR ab (Status 200,
 * abgerissener Body).
 */
const PROJEKT_SEITE = 50;

export interface Koordinatenladung {
    koordinaten: ProjektKoordinaten;
    /**
     * Der Kunden-Ersatzweg ist ausgefallen (fehlendes CRM-Modul, fehlende
     * Leserechte, Netzfehler). Die betroffenen Projekte stehen anschliessend
     * als „ohne Koordinaten" auf der Karte — das ist eine Aussage ueber den
     * Ladezustand und nicht ueber die Adressen, und genau deshalb muss sie
     * ausgewiesen werden.
     */
    ersatzwegFehler: boolean;
    /** Projekte, die auf den ausgefallenen Ersatzweg angewiesen waren. */
    ersatzwegOffen: number;
}

/**
 * Projekt-id -> Koordinate. Erst die Projektadresse
 * (`data.metadata.globalData.locationCoordinates` via projectToFeature),
 * dann ersatzweise der Kundensitz (`customers.lat/lng`). Projekte, die auf
 * keinem der beiden Wege verortet sind, fehlen hier bewusst — buildRouteDays
 * weist sie als „ohne Koordinaten" aus, statt sie zu schlucken.
 *
 * Laeuft NUR, wenn eine Karte geoeffnet wurde: fuer Tabelle und Diagramme
 * braucht es keine Koordinate, und der Select ist der teuerste der Ansicht.
 */
const ladeProjektKoordinaten = async (projektIds: string[]): Promise<Koordinatenladung> => {
    const koordinaten: ProjektKoordinaten = new Map();
    if (projektIds.length === 0) return { koordinaten, ersatzwegFehler: false, ersatzwegOffen: 0 };

    const ohneKoordinate: { projektId: string; customerId: string }[] = [];

    for (const teil of bloecke(projektIds, PROJEKT_SEITE)) {
        const { data, error } = await supabase
            .from('projects')
            .select('id, name, project_number, customer_id, metadata:data->metadata')
            .in('id', teil);
        if (error) throw new Error(describeError(error));

        (data || []).forEach((row: any) => {
            const feature = projectToFeature(row);
            if (feature) {
                koordinaten.set(String(row.id), { lat: feature.lat, lng: feature.lng });
            } else if (row.customer_id) {
                ohneKoordinate.push({ projektId: String(row.id), customerId: String(row.customer_id) });
            }
        });
    }

    if (ohneKoordinate.length === 0) return { koordinaten, ersatzwegFehler: false, ersatzwegOffen: 0 };

    const kundenIds: string[] = [];
    const gesehen = new Set<string>();
    ohneKoordinate.forEach((o) => {
        if (gesehen.has(o.customerId)) return;
        gesehen.add(o.customerId);
        kundenIds.push(o.customerId);
    });
    const kundenKoordinaten: ProjektKoordinaten = new Map();
    let ersatzwegFehler = false;
    for (const teil of bloecke(kundenIds)) {
        const { data, error } = await supabase.from('customers').select('id, name, lat, lng').in('id', teil);
        // Fehlt das CRM-Modul oder die Leserechte, bleibt der Ersatzweg aus —
        // die Projektadresse ist der Hauptweg, nicht dieser hier. Der Abbruch
        // wird aber GEMELDET: sonst behauptet die Karte anschliessend „ohne
        // Koordinaten" ueber Projekte, deren Kundensitz nie abgefragt wurde.
        if (error) {
            console.warn('Kundensitz als Ersatzkoordinate nicht ladbar', error);
            ersatzwegFehler = true;
            break;
        }
        (data || []).forEach((row: any) => {
            const feature = customerToFeature({ id: String(row.id), name: String(row.name || ''), lat: row.lat, lng: row.lng });
            if (feature) kundenKoordinaten.set(String(row.id), { lat: feature.lat, lng: feature.lng });
        });
    }

    ohneKoordinate.forEach((o) => {
        const treffer = kundenKoordinaten.get(o.customerId);
        if (treffer) koordinaten.set(o.projektId, treffer);
    });

    return {
        koordinaten,
        ersatzwegFehler,
        // Nur bei Ausfall aussagekraeftig: die Projekte, die nach dem Abbruch
        // ohne Koordinate dastehen. Ohne Ausfall sind das schlicht Kunden ohne
        // hinterlegte Lage, also ein echter Datenmangel und kein Ladefehler.
        ersatzwegOffen: ersatzwegFehler
            ? ohneKoordinate.filter((o) => !kundenKoordinaten.has(o.customerId)).length
            : 0,
    };
};

// ─── Zusammenfassen ueber mehrere Nutzer ────────────────────────────────────

const leereMatrix = (): number[][] => Array.from({ length: 7 }, () => new Array(24).fill(0));

/**
 * Serien mehrerer Nutzer zu EINER Serie addieren (Auswahl „Alle Nutzer").
 * Bewusst hier und nicht in userActivityStats.ts: das ist eine Frage der
 * Darstellung („was zeigt das Diagramm gerade"), keine der Auswertung.
 */
const fasseZusammen = (summaries: UserActivitySummary[]): UserActivityChartData => {
    const tage = new Map<string, UserActivityDay>();
    const stunden = new Array(24).fill(0) as number[];
    const matrix = leereMatrix();

    summaries.forEach((s) => {
        (s.perDay || []).forEach((d) => {
            const vorhanden = tage.get(d.day);
            if (vorhanden) {
                vorhanden.startCount += d.startCount;
                vorhanden.projectOpenCount += d.projectOpenCount;
                vorhanden.trackedMs += d.trackedMs;
            } else {
                tage.set(d.day, { ...d });
            }
        });
        (s.hourHistogram || []).forEach((v, h) => {
            if (h < 24) stunden[h] += v || 0;
        });
        (s.weekdayHourHeatmap || []).forEach((row, wd) => {
            if (wd >= 7) return;
            (row || []).forEach((v, h) => {
                if (h < 24) matrix[wd][h] += v || 0;
            });
        });
    });

    return {
        perDay: Array.from(tage.values()).sort((a, b) => a.day.localeCompare(b.day)),
        hourHistogram: stunden,
        weekdayHourHeatmap: matrix,
    };
};

/**
 * Rangliste ueber ALLE Nutzer der Auswahl. Die Zusammenfassung je Projekt
 * macht userActivityStats.topProjectsByDuration — hier werden nur die
 * Sitzungen der angezeigten Nutzer zusammengelegt.
 */
const rangliste = (summaries: UserActivitySummary[], grenze = 10): TopProjectRow[] =>
    topProjectsByDuration(
        summaries.flatMap((s) => s.projectSessions || []),
        grenze,
    );

// ─── Hook ───────────────────────────────────────────────────────────────────

export interface UseUserActivityResult {
    loading: boolean;
    error: string | null;
    /** Die Tabelle fehlt noch (Migration Nr. 46 nicht gefahren). */
    tableMissing: boolean;
    reload: () => void;

    filter: UserActivityFilter;
    setFilter: (naechster: UserActivityFilter) => void;
    /** Der Zeitraum, der wirklich abgefragt wurde — gehoert sichtbar in die UI. */
    range: Zeitfenster;

    currentUser: UserProfile | null;
    /** Auswaehlbare Nutzer, bereits company-scoped. */
    users: UserProfile[];
    /**
     * Profile, die der Firmenfilter mangels Firmenangabe verworfen hat. Ihre
     * Ereignisse werden gar nicht geladen — die Zahl gehoert sichtbar in die
     * Filterleiste, sonst liest sich der Backfill-Rueckstand als „keine Daten".
     */
    profilesWithoutCompany: number;
    /** Am eigenen Profil fehlt die Firmenangabe — sichtbar bleibt nur man selbst. */
    ownCompanyMissing: boolean;

    /** Roh-Events der Auswahl (Zeitraum + Firma + Nutzer). */
    events: UserActivityEvent[];
    /** Kein einziges Event im Zeitraum. */
    isEmpty: boolean;
    /** Die Obergrenze hat gegriffen: die Reihe endet vor dem Ende des Zeitraums. */
    truncated: boolean;
    /** Obere Schranke der Nutzer, deren Reihe gestutzt ist (Blockgroesse). */
    truncatedUserCount: number;
    /** Die geltende Obergrenze, damit die UI sie beziffern kann. */
    eventLimit: number;
    /**
     * Die Gegenstuecke an den Zeitraumraendern konnten nicht vollstaendig
     * nachgeladen werden (Obergrenze oder Abfragefehler). Dann kann ein
     * Schliessen wieder als „verworfen" dastehen, obwohl nur seine Oeffnung
     * ausserhalb des Zeitraums liegt — die Ansicht muss die Randzahlen in
     * diesem Fall mit Vorbehalt zeigen.
     */
    boundaryIncomplete: boolean;

    /** Uebersichtstabelle: eine Zeile je Nutzer mit Aktivitaet im Zeitraum. */
    rows: UserActivityRow[];
    /** Diagrammserien der aktuellen Auswahl. */
    charts: UserActivityChartData;
    topProjects: TopProjectRow[];
    /** Tagesrouten je Nutzer-id, aufsteigend nach Tag (wie buildRouteDays liefert). */
    routeDaysByUser: Record<string, RouteDay[]>;

    /** Karte geoeffnet -> Koordinaten duerfen geladen werden (vorher nicht). */
    requestRouteCoordinates: () => void;
    routeCoordinatesLoading: boolean;
    /** Koordinaten liegen vor; erst dann sind „verortet"-Zahlen belastbar. */
    routeCoordinatesLoaded: boolean;
    /** Laden der Koordinaten gescheitert — die Karte ist unvollstaendig. */
    routeCoordinatesFailed: boolean;
    /**
     * TEIL-Ausfall: die Projektadressen liegen vor, der Kundensitz als
     * Ersatzweg nicht. Betroffene Projekte stehen als „ohne Koordinaten" da,
     * ohne dass darueber etwas bekannt waere.
     */
    routeCoordinatesPartial: boolean;
    /** Projekte, die der ausgefallene Ersatzweg haette verorten koennen. */
    routeCoordinatesPartialCount: number;
}

export const useUserActivity = (): UseUserActivityResult => {
    const [filter, setFilter] = useState<UserActivityFilter>(STANDARD_FILTER);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [tableMissing, setTableMissing] = useState(false);
    const [nonce, setNonce] = useState(0);

    const [currentUser, setCurrentUser] = useState<UserProfile | null>(null);
    const [users, setUsers] = useState<UserProfile[]>([]);
    const [events, setEvents] = useState<UserActivityEvent[]>([]);
    // Getrennt gehalten: die Randereignisse liegen AUSSERHALB des Zeitraums und
    // haben in Route, Leermeldung und Koordinatenladung nichts verloren. Sie
    // dienen allein der Paarung (siehe `ladeRandereignisse`).
    const [randEvents, setRandEvents] = useState<UserActivityEvent[]>([]);
    const [randUnvollstaendig, setRandUnvollstaendig] = useState(false);
    const [truncated, setTruncated] = useState(false);
    const [truncatedUserCount, setTruncatedUserCount] = useState(0);
    const [firmenrest, setFirmenrest] = useState<Firmenfilterrest>({ verworfen: 0, eigeneFehlt: false });

    const [koordinaten, setKoordinaten] = useState<ProjektKoordinaten>(() => new Map());
    const [koordinatenAngefordert, setKoordinatenAngefordert] = useState(false);
    const [koordinatenStand, setKoordinatenStand] = useState<'aus' | 'laedt' | 'da' | 'fehler'>('aus');
    const [ersatzweg, setErsatzweg] = useState<{ fehler: boolean; offen: number }>({ fehler: false, offen: 0 });

    const reload = useCallback(() => setNonce((n) => n + 1), []);
    const requestRouteCoordinates = useCallback(() => setKoordinatenAngefordert(true), []);

    const range = useMemo(() => zeitfenster(filter), [filter]);
    const { vonIso, bisIso } = range;

    useEffect(() => {
        let abgebrochen = false;

        const laden = async () => {
            setLoading(true);
            setError(null);
            setTableMissing(false);
            setTruncated(false);
            setTruncatedUserCount(0);
            setRandUnvollstaendig(false);
            try {
                // 1. Wer bin ich, und wen darf ich sehen (Company-Scoping wie
                //    im uebrigen Admin-Bereich, AdminView.tsx:163-177).
                //    Scheitert dieser Schritt, ist das ein FEHLER und keine
                //    leere Auswertung: ohne eigenes Profil gibt es keine
                //    Firmenzuordnung, und „Keine Daten im Zeitraum" waere an
                //    dieser Stelle schlicht gelogen.
                const { data: auth, error: authFehler } = await supabase.auth.getUser();
                if (authFehler) throw new Error(describeError(authFehler));
                if (!auth?.user) throw new Error('Keine angemeldete Sitzung — die Aktivitätsdaten sind nicht zuzuordnen.');

                const { data: meins, error: meinFehler } = await supabase
                    .from('profiles')
                    .select('*')
                    .eq('id', auth.user.id)
                    .maybeSingle();
                if (meinFehler) throw new Error(describeError(meinFehler));
                const ich = (meins as UserProfile) || null;
                if (!ich) throw new Error('Das eigene Profil wurde nicht gefunden — ohne Profil ist keine Firmenzuordnung möglich.');

                const { data: profile, error: profilFehler } = await supabase
                    .from('profiles')
                    .select('*')
                    .order('last_name', { ascending: true });
                if (profilFehler) throw new Error(describeError(profilFehler));

                const alle = (profile || []) as UserProfile[];
                const sichtbar = ich.role === 'super_admin' ? alle : alle.filter((u) => gleicheFirma(u, ich));
                const sichtbareIds = sichtbar.map((u) => u.id);
                // Was der Filter weggeworfen hat, wird mitgefuehrt statt
                // verschwiegen (Filterleiste).
                const rest = firmenfilterrest(alle, ich);

                // 2. Events im Zeitraum — Zeitraum UND Nutzerkreis stehen in
                //    der Abfrage, geladen wird seitenweise.
                const zielIds =
                    filter.userId === 'all' ? sichtbareIds : sichtbareIds.filter((id) => id === filter.userId);

                let geladen: EventLadung;
                try {
                    geladen = await ladeEvents(vonIso, bisIso, zielIds);
                } catch (e) {
                    if (istTabelleFehlt(e)) {
                        if (!abgebrochen) {
                            setTableMissing(true);
                            setEvents([]);
                            setRandEvents([]);
                            setUsers(sichtbar);
                            setFirmenrest(rest);
                            setCurrentUser(ich);
                        }
                        return;
                    }
                    throw new Error(describeError(e));
                }

                if (abgebrochen) return;

                // 3. Die Gegenstuecke an den beiden Zeitraumraendern. Gezielt
                //    und nur, wenn ein Rand wirklich haengt — ohne Kandidaten
                //    faellt keine Abfrage an.
                const rand = await ladeRandereignisse(vonIso, bisIso, geladen.events, zielIds);

                if (abgebrochen) return;
                setCurrentUser(ich);
                setUsers(sichtbar);
                setFirmenrest(rest);
                setEvents(geladen.events);
                setRandEvents(rand.events);
                setRandUnvollstaendig(rand.unvollstaendig);
                setTruncated(geladen.abgeschnitten);
                setTruncatedUserCount(geladen.abgeschnitteneNutzer);
                // Neue Ereignismenge -> die Koordinaten davor gelten nicht mehr.
                // Nachgeladen wird nur, wenn eine Karte offen ist.
                setKoordinaten(new Map());
                setKoordinatenStand('aus');
                setErsatzweg({ fehler: false, offen: 0 });
            } catch (e) {
                if (!abgebrochen) {
                    setError(describeError(e));
                    setEvents([]);
                    setRandEvents([]);
                }
            } finally {
                if (!abgebrochen) setLoading(false);
            }
        };

        void laden();
        return () => {
            abgebrochen = true;
        };
    }, [vonIso, bisIso, filter.userId, nonce]);

    // ── Koordinaten: erst auf Anforderung ───────────────────────────────────
    //
    // Der Select zieht `metadata:data->metadata` je Projekt und ist damit der
    // teuerste Weg der Ansicht. Tabelle und Diagramme brauchen ihn nicht — er
    // laeuft erst, wenn eine Karte geoeffnet wurde, und danach bei jedem
    // weiteren Ladevorgang mit.
    useEffect(() => {
        if (!koordinatenAngefordert) return;

        let abgebrochen = false;

        const laden = async () => {
            const gesehen = new Set<string>();
            const projektIds: string[] = [];
            events.forEach((e) => {
                if (e.eventType !== 'project_open' || !e.projectId || gesehen.has(e.projectId)) return;
                gesehen.add(e.projectId);
                projektIds.push(e.projectId);
            });

            if (projektIds.length === 0) {
                if (!abgebrochen) {
                    setKoordinaten(new Map());
                    setKoordinatenStand('da');
                    setErsatzweg({ fehler: false, offen: 0 });
                }
                return;
            }

            setKoordinatenStand('laedt');
            try {
                const geladen = await ladeProjektKoordinaten(projektIds);
                if (!abgebrochen) {
                    setKoordinaten(geladen.koordinaten);
                    setKoordinatenStand('da');
                    // Der Hauptweg hat getragen, der Ersatzweg nicht: das ist
                    // ein TEIL-Ausfall und keine leere Karte — er wird eigens
                    // ausgewiesen, damit die betroffenen Projekte nicht als
                    // „ohne Koordinaten" durchgehen.
                    setErsatzweg({ fehler: geladen.ersatzwegFehler, offen: geladen.ersatzwegOffen });
                }
            } catch (e) {
                // Die Karte ist Beiwerk: faellt sie aus, bleibt die Auswertung
                // stehen. Der Ausfall wird aber ausgewiesen — sonst weist die
                // Karte anschliessend JEDES Projekt als „ohne Koordinaten" aus
                // und behauptet damit einen Datenmangel, den es nicht gibt.
                console.warn('Koordinaten der Tagesroute nicht ladbar', e);
                if (!abgebrochen) {
                    setKoordinaten(new Map());
                    setKoordinatenStand('fehler');
                    // Vollausfall: der Teil-Hinweis waere daneben nur Rauschen.
                    setErsatzweg({ fehler: false, offen: 0 });
                }
            }
        };

        void laden();
        return () => {
            abgebrochen = true;
        };
    }, [koordinatenAngefordert, events]);

    // ── Ableitungen (pur, in userActivityStats) ─────────────────────────────

    // Fensterreihe PLUS Randereignisse: gepaart wird ueber die Grenze hinweg,
    // gezaehlt wird nur, was im Zeitraum geoeffnet wurde — die Auswahl trifft
    // `summarizeAllUsers` anhand des Zeitfensters, nicht diese Liste.
    const paarungsEvents = useMemo(
        () => (randEvents.length === 0 ? events : [...events, ...randEvents].sort(nachZeitpunkt)),
        [events, randEvents],
    );

    const summaries = useMemo(
        () => summarizeAllUsers(paarungsEvents, { fromIso: vonIso, toIso: bisIso }),
        [paarungsEvents, vonIso, bisIso],
    );

    const nameNach = useMemo(() => {
        const map = new Map<string, UserProfile>();
        users.forEach((u) => map.set(u.id, u));
        return map;
    }, [users]);

    const rows = useMemo<UserActivityRow[]>(
        () =>
            summaries.map((s) => {
                const profil = nameNach.get(s.userId) || null;
                return {
                    userId: s.userId,
                    name: nutzerName(profil),
                    email: (profil as any)?.email || null,
                    summary: s,
                    avgDurationMs: averageSessionMs(s.projectSessions),
                };
            }),
        [summaries, nameNach],
    );

    // Die Tagesreihe wird auf den gewaehlten Zeitraum aufgefuellt: ein
    // Balkendiagramm, das leere Tage ueberspringt, staucht die Achse und
    // behauptet eine Aktivitaet, die es an diesen Tagen nicht gab.
    // Ohne untere Grenze (freier Zeitraum mit offenem Anfang) beginnt die Achse
    // beim ersten Tag mit Aktivitaet — das IST dort das erste Ereignis.
    const charts = useMemo(() => {
        const zusammen = fasseZusammen(summaries);
        const vonTag = vonIso ? localDayKey(vonIso) : zusammen.perDay[0]?.day || '';
        const bisTag = localDayKey(bisIso);
        return vonTag && bisTag
            ? { ...zusammen, perDay: fillDayRange(zusammen.perDay, vonTag, bisTag) }
            : zusammen;
    }, [summaries, vonIso, bisIso]);
    const topProjects = useMemo(() => rangliste(summaries), [summaries]);

    const routeDaysByUser = useMemo(() => {
        const nach: Record<string, RouteDay[]> = {};
        summaries.forEach((s) => {
            const eigene = events.filter((e) => e.userId === s.userId);
            nach[s.userId] = buildRouteDays(eigene, koordinaten);
        });
        return nach;
    }, [summaries, events, koordinaten]);

    return {
        loading,
        error,
        tableMissing,
        reload,
        filter,
        setFilter,
        range,
        currentUser,
        users,
        profilesWithoutCompany: firmenrest.verworfen,
        ownCompanyMissing: firmenrest.eigeneFehlt,
        events,
        isEmpty: !loading && !error && events.length === 0,
        truncated,
        truncatedUserCount,
        eventLimit: EVENT_OBERGRENZE,
        boundaryIncomplete: randUnvollstaendig,
        rows,
        charts,
        topProjects,
        routeDaysByUser,
        requestRouteCoordinates,
        // „aus" bei bereits angeforderter Karte heisst: der Ladeeffekt ist noch
        // nicht gelaufen. Das ist Warten, nicht „keine Koordinaten".
        routeCoordinatesLoading: koordinatenAngefordert && koordinatenStand !== 'da' && koordinatenStand !== 'fehler',
        routeCoordinatesLoaded: koordinatenStand === 'da',
        routeCoordinatesFailed: koordinatenStand === 'fehler',
        routeCoordinatesPartial: ersatzweg.fehler,
        routeCoordinatesPartialCount: ersatzweg.offen,
    };
};
